import type { KeyDriver as TypeKeyDriver, PnlCategory } from "../../models/UtilFunctions.model";
import { revenueCategories, expenseCategories, runningCostsCategories, excludedRows } from "./Constants";

/** Compute totals across revenue / expense / running buckets. */
export function computeTotals(impactPerCategory: Record<string, number>) {
  let revenue = 0,
    expense = 0,
    running = 0;

  const REVENUE = new Set(revenueCategories);
  const EXPENSE = new Set(expenseCategories);
  const RUNNING = new Set(runningCostsCategories);

  for (const [category, value] of Object.entries(impactPerCategory)) {
    if (REVENUE.has(category)) revenue += value;
    else if (EXPENSE.has(category)) expense += value;
    else if (RUNNING.has(category)) running += value;
  }
  return { revenue, expense, running };
}

/** Update each keyDriver's `unallocated` = variance - sum(impacts for its category). */
export function updateKeyDriversUnallocated(
  keyDrivers: TypeKeyDriver[],
  impactPerCategory: Record<string, number>,
  excluded: string[] = excludedRows, // allow override; default to constants
) {
  return keyDrivers.map((driver) => {
    const impact = impactPerCategory[driver.category] ?? 0;
    const unallocated = !excluded.includes(driver.category) && driver.variance != null ? driver.variance - impact : 0;
    return { ...driver, unallocated };
  });
}

/** Compute overall unallocated = variance - (revenue - expense - running). */
export function computeOverallUnallocated(
  variance: number,
  totals: { revenue: number; expense: number; running: number },
) {
  const totalImpact = totals.revenue - totals.expense - totals.running;
  return variance - totalImpact;
}

/** Return per-category unallocated (excluding non-financial rows). */
export function filterUnallocatedPerCategory(
  data: PnlCategory[] = [],
  excluded: string[] = excludedRows,
): { category: string; unallocated: number }[] {
  return data
    .filter((item) => !excluded.includes(item.category))
    .map((item) => ({
      category: item.category,
      unallocated: item.unallocated,
    }));
}
